import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Rating,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Star, Sort } from '@mui/icons-material';

interface BidComparisonTableProps {
  bids: any[];
  request: any;
  onAcceptBid: (bid: any, services: any[], request: any) => void;
  formatCurrency: (amount: number) => string;
  renderStarRating: (rating: number, onClick?: () => void) => JSX.Element;
  getServiceCategory: (service: string) => string;
  getServiceName: (service: string) => string;
  getFrequencyLabel: (frequency: string) => string;
}

const BidComparisonTable: React.FC<BidComparisonTableProps> = ({
  bids,
  request,
  onAcceptBid,
  formatCurrency,
  renderStarRating,
  getServiceCategory,
  getServiceName,
  getFrequencyLabel,
}) => {
  const [sortBy, setSortBy] = useState<string>('chronological');
  const [showCurrentProvidersOnly, setShowCurrentProvidersOnly] = useState<boolean>(false);

  // Helper function to format UTC dates correctly (extract UTC components to avoid timezone shifts)
  // When dates are stored as UTC midnight (e.g., 2026-01-12T00:00:00.000Z), they represent the calendar date
  // We should extract UTC components directly, not convert to local time
  const formatUTCDateString = (dateInput: Date | string): string => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    // Extract UTC date components directly (these represent the calendar date)
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1; // getUTCMonth() returns 0-11, add 1 for display
    const day = date.getUTCDate();
    return `${month}/${day}/${year}`;
  };

  // Helper function to calculate monthly payment amount and first payment date for seasonal contracts
  // CRITICAL: Matches backend logic exactly (subscriptionService.calculateMonthlyPaymentAmount and billing_cycle_anchor)
  const calculateMonthlyPaymentInfo = (servicePricing: any) => {
    if (!servicePricing?.isSeasonalContract && servicePricing?.frequency !== 'seasonal-contract') {
      return null;
    }

    const seasonalDetails = servicePricing.seasonalContractDetails;
    if (!seasonalDetails) return null;

    const paymentSchedule = seasonalDetails.paymentSchedule || 'single-payment';
    if (paymentSchedule !== 'monthly') return null;

    if (!seasonalDetails.totalContractPrice || !seasonalDetails.contractStartDate || !seasonalDetails.contractEndDate) {
      return null;
    }

    try {
      const start = new Date(seasonalDetails.contractStartDate);
      const end = new Date(seasonalDetails.contractEndDate);
      
      // Calculate monthly payment amount (matches backend logic) - use UTC components
      const startsOnFirst = start.getUTCDate() === 1;
      let billingMonths: number;
      
      if (startsOnFirst) {
        // Contract starts on 1st: Include that month in count - use UTC components
        billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                       (end.getUTCMonth() - start.getUTCMonth()) + 1;
      } else {
        // Contract starts mid-month: Exclude partial first month - use UTC components
        const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
        const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
        billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                       (end.getUTCMonth() - nextMonthMonth) + 1;
      }
      
      if (billingMonths <= 0) return null;
      
      const monthlyAmount = Math.round((seasonalDetails.totalContractPrice / billingMonths) * 100) / 100;

      // Calculate first payment date (billing cycle anchor - matches backend logic) - use UTC components
      let firstPaymentDate: Date;
      if (startsOnFirst) {
        // Contract starts on 1st: Use that 1st - use UTC components
        firstPaymentDate = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
      } else {
        // Contract starts mid-month: Use next 1st of month - use UTC components
        const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
        const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
        firstPaymentDate = new Date(Date.UTC(nextMonthYear, nextMonthMonth, 1));
      }

      return {
        monthlyAmount,
        firstPaymentDate,
        totalContractPrice: seasonalDetails.totalContractPrice,
        contractStartDate: start,
        contractEndDate: end,
        serviceFrequency: seasonalDetails.serviceFrequency,
      };
    } catch (e) {
      console.error('[BID-COMPARISON] Error calculating monthly payment info:', e);
      return null;
    }
  };

  // Filter out bids that don't have any service pricing
  let validBids = bids.filter(bid => bid.servicePricing && bid.servicePricing.length > 0);
  
  // Separate expired bids - show them separately with clear labeling
  const activeBids = validBids.filter(bid => bid.status !== 'expired');
  const expiredBids = validBids.filter(bid => bid.status === 'expired');
  
  // Filter by current providers if toggle is enabled (only for active bids)
  if (showCurrentProvidersOnly) {
    validBids = activeBids.filter(bid => bid.isCurrentProvider === true);
  } else {
    validBids = activeBids; // Only show active bids in main view
  }

  // Get all unique service names from the request
  const requestedServiceNames = request.serviceType?.name?.split(', ').map((s: string) => getServiceName(s)) || [];
  const uniqueServiceNames: string[] = Array.from(new Set(requestedServiceNames));

  // Group services by category
  const lawnServiceNames = uniqueServiceNames.filter((serviceName: string) => getServiceCategory(serviceName) === 'lawn');
  const snowServiceNames = uniqueServiceNames.filter((serviceName: string) => getServiceCategory(serviceName) === 'snow');
  const otherServiceNames = uniqueServiceNames.filter((serviceName: string) => getServiceCategory(serviceName) === 'other');

  // Sort bids based on selected criteria
  const sortBids = (bids: any[]) => {
    const sortedBids = [...bids];
    
    switch (sortBy) {
      case 'price-low':
        return sortedBids.sort((a, b) => {
          const aPrice = a.servicePricing?.reduce((sum: number, sp: any) => sum + (sp.price || 0), 0) || 0;
          const bPrice = b.servicePricing?.reduce((sum: number, sp: any) => sum + (sp.price || 0), 0) || 0;
          return aPrice - bPrice;
        });
      case 'price-high':
        return sortedBids.sort((a, b) => {
          const aPrice = a.servicePricing?.reduce((sum: number, sp: any) => sum + (sp.price || 0), 0) || 0;
          const bPrice = b.servicePricing?.reduce((sum: number, sp: any) => sum + (sp.price || 0), 0) || 0;
          return bPrice - aPrice;
        });
      case 'rating-high':
        return sortedBids.sort((a, b) => {
          const aRating = a.providerId?.rating || 0;
          const bRating = b.providerId?.rating || 0;
          return bRating - aRating;
        });
      case 'rating-low':
        return sortedBids.sort((a, b) => {
          const aRating = a.providerId?.rating || 0;
          const bRating = b.providerId?.rating || 0;
          return aRating - bRating;
        });
      case 'chronological':
      default:
        return sortedBids.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
  };

  const sortedBids = sortBids(validBids);
  const displayedBids = sortedBids.slice(0, 6); // Show up to 6 bids

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Compare Provider Bids
      </Typography>
      
      {/* Sorting Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Sort sx={{ color: 'text.secondary' }} />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={handleSortChange}
          >
            <MenuItem value="chronological">Chronological (Newest First)</MenuItem>
            <MenuItem value="price-low">Price: Low to High</MenuItem>
            <MenuItem value="price-high">Price: High to Low</MenuItem>
            <MenuItem value="rating-high">Rating: High to Low</MenuItem>
            <MenuItem value="rating-low">Rating: Low to High</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={showCurrentProvidersOnly}
              onChange={(e) => setShowCurrentProvidersOnly(e.target.checked)}
              size="small"
            />
          }
          label="Show Current Providers Only"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {displayedBids.length} provider{displayedBids.length !== 1 ? 's' : ''} shown
        </Typography>
      </Box>
      
      {displayedBids.length > 2 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          ← Scroll horizontally to see all providers →
        </Typography>
      )}
      
      {/* Provider Windows - Side by Side */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        flexWrap: 'nowrap', 
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        overflowX: 'auto',
        pb: 2,
        px: 1,
        width: '100%',
        minHeight: '500px',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
        }
      }}>
        {displayedBids.map((bid: any, index: number) => (
          <Paper 
            key={bid._id} 
            elevation={4} 
            sx={{ 
              p: 2,
              backgroundColor: index % 2 === 0 ? 'primary.50' : 'secondary.50',
              border: 2,
              borderColor: index % 2 === 0 ? 'primary.main' : 'secondary.main',
              borderRadius: 2,
              height: '100%',
              minWidth: '300px',
              maxWidth: '400px',
              width: '300px',
              minHeight: '450px',
              position: 'relative',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            {/* Provider Header */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: 2, 
              pb: 2, 
              borderBottom: 2, 
              borderColor: index % 2 === 0 ? 'primary.light' : 'secondary.light' 
            }}>
              <Typography variant="h5" fontWeight="bold" sx={{ 
                mb: 1, 
                color: index % 2 === 0 ? 'primary.main' : 'secondary.main' 
              }}>
                {bid.providerId?.businessName || 'Provider'}
              </Typography>
              
              {bid.providerId?.rating && bid.providerId.rating > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                  <Rating value={bid.providerId.rating} readOnly precision={0.5} emptyIcon={<Star style={{ opacity: 0.55 }} fontSize="inherit" />} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({bid.providerId.rating.toFixed(1)})
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No reviews yet
                </Typography>
              )}
              
              <Chip
                label={bid.status === 'expired' ? 'EXPIRED' : (bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1))}
                size="small"
                color={bid.status === 'expired' ? 'error' : (bid.status === 'pending' ? 'warning' : 'default')}
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Service Categories */}
            <Box sx={{ mb: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Lawn Services */}
              {lawnServiceNames.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, backgroundColor: 'success.50', borderColor: 'success.200', flex: 1 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1.2rem' }}>🌱</span>
                      <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                        Lawn Services
                      </Typography>
                      {request.baggingRequired && (
                        <Chip label="Bagging Requested" size="small" color="default" variant="outlined" />
                      )}
                    </Box>
                    
                    {lawnServiceNames.map((serviceName: string) => {
                      const servicePricing = bid.servicePricing.find((sp: any) => getServiceName(sp.service) === serviceName);
                      const monthlyPaymentInfo = servicePricing ? calculateMonthlyPaymentInfo(servicePricing) : null;
                      const isSeasonalContract = servicePricing?.isSeasonalContract || servicePricing?.frequency === 'seasonal-contract';
                      const seasonalDetails = servicePricing?.seasonalContractDetails;
                      
                      return (
                        <Box key={serviceName} sx={{ mb: 1.5, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="medium">
                              {getServiceName(serviceName)}
                            </Typography>
                            {request.baggingRequired && (
                              <Chip label="Bagging" size="small" color="default" variant="outlined" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getFrequencyLabel(servicePricing?.frequency || request.frequency)}
                          </Typography>
                          {isSeasonalContract && (
                            <Chip
                              label="Seasonal Contract"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5, mb: 0.5 }}
                            />
                          )}
                          {servicePricing ? (
                            <Box sx={{ mt: 0.5 }}>
                              {isSeasonalContract && seasonalDetails?.totalContractPrice ? (
                                <>
                                  <Typography variant="h6" color="success.dark" fontWeight="bold">
                                    {formatCurrency(seasonalDetails.totalContractPrice)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Total Contract
                                  </Typography>
                                </>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" color="success.dark" fontWeight="bold">
                                    {formatCurrency(servicePricing.price)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {servicePricing.duration}h
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Monthly Payment Information */}
                              {monthlyPaymentInfo && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark">
                                    {formatCurrency(monthlyPaymentInfo.monthlyAmount)} per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Contract: {formatUTCDateString(monthlyPaymentInfo.contractStartDate)} - {formatUTCDateString(monthlyPaymentInfo.contractEndDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {isSeasonalContract && seasonalDetails && !monthlyPaymentInfo && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Payment: <strong>One-Time Payment (full payment after first service)</strong>
                                  </Typography>
                                  {seasonalDetails.contractStartDate && seasonalDetails.contractEndDate && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                      Contract Period: <strong>{formatUTCDateString(seasonalDetails.contractStartDate)} - {formatUTCDateString(seasonalDetails.contractEndDate)}</strong>
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      size="small"
                      disabled={bid.status === 'expired'}
                      onClick={() => onAcceptBid(bid, bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'lawn'), request)}
                      sx={{ mt: 1 }}
                    >
                      {bid.status === 'expired' ? 'Expired - Cannot Accept' : 'Accept Lawn Services'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Snow Services */}
              {snowServiceNames.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, backgroundColor: 'info.50', borderColor: 'info.200', flex: 1 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <span style={{ fontSize: '1.2rem' }}>❄️</span>
                      <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                        Snow Removal
                      </Typography>
                    </Box>
                    
                    {snowServiceNames.map((serviceName: string) => {
                      const servicePricing = bid.servicePricing.find((sp: any) => getServiceName(sp.service) === serviceName);
                      const monthlyPaymentInfo = servicePricing ? calculateMonthlyPaymentInfo(servicePricing) : null;
                      const isSeasonalContract = servicePricing?.isSeasonalContract || servicePricing?.frequency === 'seasonal-contract';
                      const seasonalDetails = servicePricing?.seasonalContractDetails;
                      
                      return (
                        <Box key={serviceName} sx={{ mb: 1.5, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {getServiceName(serviceName)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getFrequencyLabel(servicePricing?.frequency || request.frequency)}
                          </Typography>
                          {isSeasonalContract && (
                            <Chip
                              label="Seasonal Contract"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5, mb: 0.5 }}
                            />
                          )}
                          {servicePricing ? (
                            <Box sx={{ mt: 0.5 }}>
                              {isSeasonalContract && seasonalDetails?.totalContractPrice ? (
                                <>
                                  <Typography variant="h6" color="info.dark" fontWeight="bold">
                                    {formatCurrency(seasonalDetails.totalContractPrice)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Total Contract
                                  </Typography>
                                </>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" color="info.dark" fontWeight="bold">
                                    {formatCurrency(servicePricing.price)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {servicePricing.duration}h
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Monthly Payment Information */}
                              {monthlyPaymentInfo && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark">
                                    {formatCurrency(monthlyPaymentInfo.monthlyAmount)} per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Contract: {formatUTCDateString(monthlyPaymentInfo.contractStartDate)} - {formatUTCDateString(monthlyPaymentInfo.contractEndDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {isSeasonalContract && seasonalDetails && !monthlyPaymentInfo && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Payment: <strong>One-Time Payment (full payment after first service)</strong>
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                    
                    <Button
                      variant="contained"
                      color="info"
                      fullWidth
                      size="small"
                      disabled={bid.status === 'expired'}
                      onClick={() => onAcceptBid(bid, bid.servicePricing.filter((sp: any) => getServiceCategory(sp.service) === 'snow'), request)}
                      sx={{ mt: 1 }}
                    >
                      {bid.status === 'expired' ? 'Expired - Cannot Accept' : 'Accept Snow Removal'}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Other Services */}
              {otherServiceNames.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2, backgroundColor: 'grey.50', borderColor: 'grey.300', flex: 1 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <span style={{ fontSize: '1.2rem' }}>🔧</span>
                      <Typography variant="subtitle1" fontWeight="bold" color="grey.700">
                        Other Services
                      </Typography>
                    </Box>
                    
                    {otherServiceNames.map((serviceName: string) => {
                      const servicePricing = bid.servicePricing.find((sp: any) => getServiceName(sp.service) === serviceName);
                      const monthlyPaymentInfo = servicePricing ? calculateMonthlyPaymentInfo(servicePricing) : null;
                      const isSeasonalContract = servicePricing?.isSeasonalContract || servicePricing?.frequency === 'seasonal-contract';
                      const seasonalDetails = servicePricing?.seasonalContractDetails;
                      
                      return (
                        <Box key={serviceName} sx={{ mb: 1.5, p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {getServiceName(serviceName)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getFrequencyLabel(servicePricing?.frequency || request.frequency)}
                          </Typography>
                          {isSeasonalContract && (
                            <Chip
                              label="Seasonal Contract"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mt: 0.5, mb: 0.5 }}
                            />
                          )}
                          {servicePricing ? (
                            <Box sx={{ mt: 0.5 }}>
                              {isSeasonalContract && seasonalDetails?.totalContractPrice ? (
                                <>
                                  <Typography variant="h6" color="grey.700" fontWeight="bold">
                                    {formatCurrency(seasonalDetails.totalContractPrice)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Total Contract
                                  </Typography>
                                </>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="h6" color="grey.700" fontWeight="bold">
                                    {formatCurrency(servicePricing.price)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {servicePricing.duration}h
                                  </Typography>
                                </Box>
                              )}
                              
                              {/* Monthly Payment Information */}
                              {monthlyPaymentInfo && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                    Monthly Payment Plan
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" color="primary.dark">
                                    {formatCurrency(monthlyPaymentInfo.monthlyAmount)} per month
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    First Payment: <strong>{formatUTCDateString(monthlyPaymentInfo.firstPaymentDate)}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    Contract: {formatUTCDateString(monthlyPaymentInfo.contractStartDate)} - {formatUTCDateString(monthlyPaymentInfo.contractEndDate)}
                                  </Typography>
                                </Box>
                              )}
                              
                              {isSeasonalContract && seasonalDetails && !monthlyPaymentInfo && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  Payment: <strong>One-Time Payment (full payment after first service)</strong>
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Accept All Button removed - Snow Removal is hidden, only lawn services available */}
          </Paper>
        ))}
      </Box>

      {/* Show message if no active bids */}
      {displayedBids.length === 0 && expiredBids.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No bids available for comparison
          </Typography>
        </Box>
      )}

      {/* Expired Bids Section */}
      {expiredBids.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="error.main" gutterBottom>
              Expired Bids ({expiredBids.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These bids have expired after 7 days and can no longer be accepted. Submit a new service request to receive fresh bids.
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
            }
          }}>
            {expiredBids.slice(0, 6).map((bid: any, index: number) => (
              <Paper 
                key={bid._id} 
                elevation={2} 
                sx={{ 
                  p: 2,
                  backgroundColor: 'error.50',
                  border: 2,
                  borderColor: 'error.main',
                  borderRadius: 2,
                  height: '100%',
                  minWidth: '300px',
                  maxWidth: '400px',
                  width: '300px',
                  opacity: 0.8,
                  flexShrink: 0
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 2, pb: 2, borderBottom: 2, borderColor: 'error.light' }}>
                  <Typography variant="h6" fontWeight="bold" color="error.main" sx={{ mb: 1 }}>
                    {bid.providerId?.businessName || 'Provider'}
                  </Typography>
                  <Chip
                    label="EXPIRED"
                    size="small"
                    color="error"
                    sx={{ mb: 2 }}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Expired on:</strong> {bid.validUntil ? new Date(bid.validUntil).toLocaleDateString() : (bid.createdAt ? new Date(new Date(bid.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString() : 'N/A')}
                  </Typography>
                  <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
                    This bid can no longer be accepted. Submit a new service request for fresh bids.
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default BidComparisonTable;